import { logger } from "@/utils/logger";
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import SecureStorage from '../utils/secureStorage';
import { initializeSessionBoundary } from '../utils/sessionBoundary';

// Complete application state interface
export interface AppState {
  // Chat state
  isExpanded: boolean;
  showNewConversationDialog: boolean;
  
  // Messages
  messages: Array<{
    id: string;
    content: string;
    isUser: boolean;
    timestamp: Date;
    formData?: { [key: string]: string };
    aiResponseReceivedAt?: Date;
  }>;
  
  // Form state
  activeForm: string | null;
  isOfferHighlighted: boolean;
  formData: { [key: string]: any };
  aiFormData: { [key: string]: any };
  
  // Session data
  sessionId: string | null;
  chatId: string | null;
  dealerId: string | null;
  
  // Other state
  vehicleInterest: any;
  scrollPosition: number;
  autoExpandDisabled: boolean;
  
  // Metadata
  lastUpdated: number;
  url: string;
}

const DEFAULT_STATE: AppState = {
  isExpanded: false,
  showNewConversationDialog: false,
  messages: [],
  activeForm: null,
  isOfferHighlighted: false,
  formData: {},
  aiFormData: {},
  sessionId: null,
  chatId: null,
  dealerId: null,
  vehicleInterest: null,
  scrollPosition: 0,
  autoExpandDisabled: false,
  lastUpdated: Date.now(),
  url: ''
};

const STATE_KEY = 'app_complete_state';
const SAVE_DEBOUNCE_MS = 500;

/**
 * One-level value equality, used by `updateState` to detect no-op writes.
 * Callers frequently pass freshly-built literals (`formData: {}`, `{...prev, ...data}`,
 * `messages: [...]`) for values that have not actually changed, so comparing by
 * reference alone would still let a no-op write force a re-render.
 */
const shallowEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(key =>
    Object.prototype.hasOwnProperty.call(b, key) &&
    Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
  );
};

// Global state to prevent multiple instances
let globalState: AppState = DEFAULT_STATE;
let globalIsRestoring = true;
let globalInitialized = false;
let globalInitializing = false; // New flag to prevent race conditions
let stateSubscribers: Array<(state: AppState) => void> = [];
let restoringSubscribers: Array<(isRestoring: boolean) => void> = [];
let saveTimeout: NodeJS.Timeout | null = null; // Add debounce timeout

export const useStateManager = () => {
  const [state, setState] = useState<AppState>(globalState);
  const [isRestoring, setIsRestoring] = useState(globalIsRestoring);
  const isInitializedRef = useRef(false);

  // Subscribe to global state changes
  useEffect(() => {
    // Subscribe to state changes
    const unsubscribeState = (() => {
      stateSubscribers.push(setState);
      return () => {
        stateSubscribers = stateSubscribers.filter(sub => sub !== setState);
      };
    })();

    const unsubscribeRestoring = (() => {
      restoringSubscribers.push(setIsRestoring);
      return () => {
        restoringSubscribers = restoringSubscribers.filter(sub => sub !== setIsRestoring);
      };
    })();

    return () => {
      unsubscribeState();
      unsubscribeRestoring();
    };
  }, []);

  // Load state from encrypted storage on mount - only once globally
  useEffect(() => {
    // Prevent multiple initializations
    if (globalInitialized || globalInitializing) {
      return;
    }
    
    // Set flag immediately
    globalInitializing = true;

    const loadState = async () => {
      try {
        // Initialize session boundary
        initializeSessionBoundary();
        
        globalIsRestoring = true;
        restoringSubscribers.forEach(sub => sub(true));
        
        const savedState = await SecureStorage.getItem(STATE_KEY);
        
        // CRITICAL FALLBACK: If no messages in savedState, try to recover from localStorage backup
        let fallbackMessages: any[] = [];
        if (!savedState?.messages?.length) {
          try {
            const backupMessages = localStorage.getItem('d2c_chat_messages_backup');
            if (backupMessages) {
              const parsed = JSON.parse(backupMessages);
              if (Array.isArray(parsed) && parsed.length > 0) {
                logger.log('🔄 STATE: Recovering messages from localStorage backup:', parsed.length, 'messages');
                fallbackMessages = parsed.map((msg: any, index: number) => ({
                  id: msg.id || `recovered_${index}_${Date.now()}`,
                  content: msg.content,
                  isUser: msg.role === 'user',
                  timestamp: new Date(),
                  aiResponseReceivedAt: msg.role !== 'user' ? new Date() : undefined,
                  skipIntroAndClosing: true // Skip re-parsing for recovered messages
                }));
              }
            }
          } catch (backupError) {
            logger.warn('⚠️ STATE: Failed to recover from localStorage backup:', backupError);
          }
        }
        
        if (savedState && typeof savedState === 'object') {
          
          // Convert timestamp strings/numbers back to Date objects
          // Use fallback messages if savedState has none
          const messageSource = savedState.messages?.length > 0 ? savedState.messages : fallbackMessages;
          const restoredMessages = messageSource.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp || Date.now()),
            aiResponseReceivedAt: msg.aiResponseReceivedAt ? new Date(msg.aiResponseReceivedAt) : undefined,
            skipIntroAndClosing: msg.skipIntroAndClosing ?? false
          }));
          
          const restoredState = {
            ...DEFAULT_STATE,
            ...savedState,
            messages: restoredMessages,
            lastUpdated: Date.now(),
            // CRITICAL: Never restore form state from previous sessions
            // Forms should always start fresh to prevent activation loops
            activeForm: null,
            isOfferHighlighted: false,
            formData: {},
            aiFormData: {},
            // Reset autoExpandDisabled if starting fresh session (no messages)
            autoExpandDisabled: restoredMessages.length > 0 ? savedState.autoExpandDisabled : false,
          
          // CRITICAL: Restore session IDs so the conversation persists across page loads
          sessionId: savedState.sessionId || null,
          chatId: savedState.chatId || null,
          dealerId: savedState.dealerId || null
        };
        
        // On mobile, if we're coming from external navigation, force chat to be collapsed
        // Only check this once per session to prevent infinite loops
        if (!sessionStorage.getItem('mobile-navigation-checked')) {
            const isMobile = window.innerWidth <= 768;
            const referrer = document.referrer;
            const currentOrigin = window.location.origin;
            const isExternalNavigation = referrer && !referrer.startsWith(currentOrigin);
            
          if (isMobile && isExternalNavigation) {
            restoredState.isExpanded = false;
            restoredState.autoExpandDisabled = true;
          }
          sessionStorage.setItem('mobile-navigation-checked', 'true');
        }
        
        globalState = restoredState;
        stateSubscribers.forEach(sub => sub(restoredState));
        } else if (fallbackMessages.length > 0) {
          // No saved state but we recovered messages from backup
          logger.log('🔄 STATE: No saved state but recovered', fallbackMessages.length, 'messages from backup');
          const restoredState = {
            ...DEFAULT_STATE,
            messages: fallbackMessages,
            lastUpdated: Date.now()
          };
          globalState = restoredState;
          stateSubscribers.forEach(sub => sub(restoredState));
        } else {
          logger.log('⚠️ STATE: No saved state and no backup messages, using defaults');
          setState(DEFAULT_STATE);
        }
    } catch (error) {
      console.error('❌ STATE: Failed to restore encrypted state:', error);
      // Try to fall back to sync storage
      try {
        const fallbackState = SecureStorage.getItemSync(STATE_KEY);
        if (fallbackState && typeof fallbackState === 'object') {
            const restoredMessages = fallbackState.messages?.map((msg: any) => ({
              ...msg,
              timestamp: new Date(msg.timestamp),
              aiResponseReceivedAt: msg.aiResponseReceivedAt ? new Date(msg.aiResponseReceivedAt) : undefined,
              skipIntroAndClosing: msg.skipIntroAndClosing ?? false
            })) || [];
            
            const fallbackRestoredState = {
              ...DEFAULT_STATE,
              ...fallbackState,
              messages: restoredMessages,
              lastUpdated: Date.now(),
              // CRITICAL: Never restore form state from previous sessions
              // Forms should always start fresh to prevent activation loops
              activeForm: null,
              isOfferHighlighted: false,
              formData: {},
              aiFormData: {},
              // Reset autoExpandDisabled if starting fresh session (no messages)
              autoExpandDisabled: restoredMessages.length > 0 ? fallbackState.autoExpandDisabled : false,
              // CRITICAL: Restore session IDs so the conversation persists across page loads
              sessionId: fallbackState.sessionId || null,
              chatId: fallbackState.chatId || null,
              dealerId: fallbackState.dealerId || null
            };
          globalState = fallbackRestoredState;
          stateSubscribers.forEach(sub => sub(fallbackRestoredState));
        } else {
          // Try localStorage backup as last resort
          try {
            const backupMessages = localStorage.getItem('d2c_chat_messages_backup');
            if (backupMessages) {
              const parsed = JSON.parse(backupMessages);
              if (Array.isArray(parsed) && parsed.length > 0) {
                logger.log('🔄 STATE: Fallback - recovering from localStorage backup:', parsed.length, 'messages');
                const recoveredMessages = parsed.map((msg: any, index: number) => ({
                  id: msg.id || `recovered_${index}_${Date.now()}`,
                  content: msg.content,
                  isUser: msg.role === 'user',
                  timestamp: new Date(),
                  aiResponseReceivedAt: msg.role !== 'user' ? new Date() : undefined,
                  skipIntroAndClosing: true
                }));
                const recoveredState = { ...DEFAULT_STATE, messages: recoveredMessages };
                globalState = recoveredState;
                stateSubscribers.forEach(sub => sub(recoveredState));
              } else {
                globalState = DEFAULT_STATE;
                stateSubscribers.forEach(sub => sub(DEFAULT_STATE));
              }
            } else {
              globalState = DEFAULT_STATE;
              stateSubscribers.forEach(sub => sub(DEFAULT_STATE));
            }
          } catch {
            globalState = DEFAULT_STATE;
            stateSubscribers.forEach(sub => sub(DEFAULT_STATE));
          }
        }
        } catch (fallbackError) {
          console.error('❌ STATE: Fallback state restoration also failed:', fallbackError);
          // Final attempt: try localStorage backup
          try {
            const backupMessages = localStorage.getItem('d2c_chat_messages_backup');
            if (backupMessages) {
              const parsed = JSON.parse(backupMessages);
              if (Array.isArray(parsed) && parsed.length > 0) {
                logger.log('🔄 STATE: Emergency - recovering from localStorage backup:', parsed.length, 'messages');
                const recoveredMessages = parsed.map((msg: any, index: number) => ({
                  id: msg.id || `recovered_${index}_${Date.now()}`,
                  content: msg.content,
                  isUser: msg.role === 'user',
                  timestamp: new Date(),
                  aiResponseReceivedAt: msg.role !== 'user' ? new Date() : undefined,
                  skipIntroAndClosing: true
                }));
                const recoveredState = { ...DEFAULT_STATE, messages: recoveredMessages };
                globalState = recoveredState;
                stateSubscribers.forEach(sub => sub(recoveredState));
              } else {
                globalState = DEFAULT_STATE;
                stateSubscribers.forEach(sub => sub(DEFAULT_STATE));
              }
            } else {
              globalState = DEFAULT_STATE;
              stateSubscribers.forEach(sub => sub(DEFAULT_STATE));
            }
          } catch {
            globalState = DEFAULT_STATE;
            stateSubscribers.forEach(sub => sub(DEFAULT_STATE));
          }
        }
      } finally {
        globalIsRestoring = false;
        globalInitialized = true;
        globalInitializing = false;
        restoringSubscribers.forEach(sub => sub(false));
        isInitializedRef.current = true;
      }
    };

    loadState().catch(err => {
      console.error('🚨 STATE INIT: Unhandled error in loadState:', err);
      // Force completion even on error
      globalIsRestoring = false;
      globalInitialized = true;
      globalInitializing = false;
      restoringSubscribers.forEach(sub => sub(false));
    });
  }, []); // Empty deps array ensures this only runs once

  // Save state function
  const saveState = useCallback(async (newState: AppState) => {
    const stateToSave = {
      ...newState,
      lastUpdated: Date.now(),
      url: window.location.href
    };

    try {
      await SecureStorage.setItem(STATE_KEY, stateToSave);
    } catch (error) {
      console.error('❌ STATE: Failed to save encrypted state:', error);
      // Fallback to sync storage
      try {
        SecureStorage.setItemSync(STATE_KEY, stateToSave);
        logger.log('✅ STATE: Saved app state (fallback sync)');
      } catch (fallbackError) {
        console.error('❌ STATE: Failed to save state even with fallback:', fallbackError);
      }
    }
  }, []);

  // Update state with debounced save
  const updateState = useCallback((updates: Partial<AppState>) => {
    // Bail out on semantic no-ops.
    //
    // This function used to mint a new object with a fresh `lastUpdated` on every
    // call and push it into every subscriber's raw setState, so React's Object.is
    // bail-out could never engage for any consumer. That turned any effect with an
    // unstable dependency into an *unbounded* render loop instead of a self-limiting
    // one. Callers routinely pass fresh literals for unchanged values (e.g.
    // `formData: {}` from closeForm), so a reference-only comparison is not enough —
    // one level of value equality is required.
    const noop = (Object.keys(updates) as (keyof AppState)[])
      .every(key => shallowEqual(globalState[key], updates[key]));
    if (noop) {
      // Deliberately do not touch globalState, lastUpdated, the subscribers, or the
      // debounced save timer — re-arming the save timer on every no-op was also
      // starving state persistence.
      return;
    }

    const newState = { ...globalState, ...updates, lastUpdated: Date.now() };
    globalState = newState;
    stateSubscribers.forEach(sub => sub(newState));

    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    // Debounce the save operation
    saveTimeout = setTimeout(() => {
      saveState(newState);
      saveTimeout = null;
    }, SAVE_DEBOUNCE_MS);
  }, [saveState]);

  // Restore scroll position on mount if available
  useEffect(() => {
    if (state.scrollPosition > 0) {
      setTimeout(() => {
        const chatContainer = document.querySelector('[data-chat-messages]');
        if (chatContainer) {
          chatContainer.scrollTop = state.scrollPosition;
        }
      }, 100);
    }
  }, []); // Only run once on mount

  // Save state before page unload - optimized to prevent delays
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isInitializedRef.current) {
        try {
          // Quick synchronous save with minimal processing
          const finalState = {
            ...globalState, // Use global state instead of component state for speed
            scrollPosition: document.querySelector('[data-chat-messages]')?.scrollTop || 0,
            lastUpdated: Date.now(),
            url: window.location.href
          };
          
          SecureStorage.setItemSync(STATE_KEY, finalState);
          logger.log('✅ STATE: Saved state before unload');
        } catch (error) {
          // Silent error to prevent blocking unload
          console.error('❌ STATE: Failed to save state before unload:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []); // Remove state dependency to prevent re-adding listeners

  // Clear all state
  const clearState = useCallback(async () => {
    // Clear from storage immediately and synchronously
    try {
      const storageKey = `secure_${STATE_KEY}`;
      localStorage.removeItem(storageKey);
      logger.log('✅ STATE: Synchronously removed state from storage');
    } catch (error) {
      console.error('❌ STATE: Failed to remove state synchronously:', error);
    }
    
    // Clear sessionStorage items related to auto-expand
    try {
      sessionStorage.removeItem('auto-expand-mobile-checked');
      sessionStorage.removeItem('mobile-navigation-checked');
      logger.log('✅ STATE: Cleared sessionStorage auto-expand flags');
    } catch (error) {
      console.error('❌ STATE: Failed to clear sessionStorage:', error);
    }
    
    // Also use async removal method
    await SecureStorage.removeItem(STATE_KEY);
    
    globalState = DEFAULT_STATE;
    stateSubscribers.forEach(sub => sub(DEFAULT_STATE));
    logger.log('🗑️ STATE: Cleared state and reset to defaults');
  }, []);

  // Specific update functions for convenience
  const updateChatState = useCallback((updates: Partial<Pick<AppState, 'isExpanded' | 'showNewConversationDialog'>>) => {
    updateState(updates);
  }, [updateState]);

  const updateMessages = useCallback((messages: any[]) => {
    updateState({ messages });
  }, [updateState]);

  const addMessage = useCallback((message: any) => {
    // CRITICAL: Always read from the actual globalState, not a stale closure
    // This prevents race conditions when multiple messages are added quickly
    const currentMessages = globalState.messages || [];
    
    // Check for duplicate before adding (same ID or same content within 1 second)
    const isDuplicate = currentMessages.some((existing: any) => 
      existing.id === message.id ||
      (existing.content === message.content && 
       existing.isUser === message.isUser &&
       Math.abs(new Date(existing.timestamp).getTime() - new Date(message.timestamp).getTime()) < 1000)
    );
    
    if (isDuplicate) {
      logger.log('🚫 STATE: Skipping duplicate message in addMessage');
      return;
    }
    
    const newMessages = [...currentMessages, message];
    const newState = { ...globalState, messages: newMessages };
    globalState = newState;
    stateSubscribers.forEach(sub => sub(newState));
    
    
    // Save immediately - critical for message persistence
    if (isInitializedRef.current) {
      saveState(newState).catch(error => 
        console.error('❌ STATE: Failed to save message:', error)
      );
    }
  }, [saveState]);

  const updateFormState = useCallback(
    (
      updates:
        | Partial<Pick<AppState, 'activeForm' | 'isOfferHighlighted' | 'formData' | 'aiFormData'>>
        | ((prev: AppState) => Partial<Pick<AppState, 'activeForm' | 'isOfferHighlighted' | 'formData' | 'aiFormData'>>)
    ) => {
      if (typeof updates === 'function') {
        const formUpdates = updates(globalState);
        updateState(formUpdates);
      } else {
        updateState(updates);
      }
    },
    [updateState]
  );

  const updateSessionData = useCallback((updates: Partial<Pick<AppState, 'sessionId' | 'chatId' | 'dealerId'>>) => {
    updateState(updates);
  }, [updateState]);

  return {
    // State
    state,
    isRestoring,
    
    // General updates
    updateState,
    clearState,
    
    // Specific updates
    updateChatState,
    updateMessages,
    addMessage,
    updateFormState,
    updateSessionData,
  };
};
